// カードカテゴリー
export type CardCategory = 
  | 'castle'   // 城
  | 'city'     // シティ
  | 'space'    // 宇宙
  | 'movie'    // 映画
  | 'ship'     // 難破船
  | 'ghost'    // 幽霊
  | 'park';    // 遊園地

// カードティア (A=弱い/安い, B=普通, C=強い/高い)
export type CardTier = 'A' | 'B' | 'C';

// カードレアリティ
export type CardRarity = 'common' | 'rare' | 'epic' | 'legendary';

// 能力発動タイミング
export type AbilityTiming = 
  | 'onPlay'        // プレイ時(カードをめくった瞬間)
  | 'onBench'       // ベンチ時(常時効果)
  | 'onBenchCheck'  // ベンチチェック(条件判定)
  | 'onFlagGet'     // フラグ取得時
  | 'onFlagLost'    // フラグ喪失時
  | 'onDeckCheck'   // 山札チェック
  | 'onTrophy'      // トロフィー条件
  | 'onSelect'      // デッキ構築で選択時
  | 'passive'       // 常時発動
  // 旧互換
  | 'onEnter' 
  | 'onExit' 
  | 'onFlag';

// 能力効果の種類
export type AbilityEffectType =
  | 'powerBoost'      // パワー増減(永続)
  | 'powerTemp'       // パワー増減(一時的、次のカードのみ)
  | 'move'            // カード移動
  | 'revive'          // 復活(指定先へ移動)
  | 'addCard'         // 山札からカード追加
  | 'swap'            // デッキ並び替え
  | 'addTrophy'       // トロフィー追加
  // 旧互換
  | 'benchExpand'
  | 'benchBoost'
  | 'discard'
  | 'steal'
  | 'synergy'
  | 'custom';

// 移動先
export type Destination = 
  | 'deck'        // デッキ(ランダム位置)
  | 'deckTop'     // デッキ上
  | 'deckBottom'  // デッキ下
  | 'bench'       // ベンチ
  | 'exile';      // 除外

// 能力効果
export interface AbilityEffect {
  type: AbilityEffectType;
  value?: number;
  targetCategory?: CardCategory | 'all';  // 'all' = 全属性対象
  targetPlayer?: 'self' | 'opponent';
  targetPower?: number;          // 対象パワー(basePowerが一致するカード)
  destination?: Destination;
  addCardTier?: CardTier;
  addCardSource?: 'deck' | 'tierA' | 'tierB' | 'tierC';
  swapMode?: 'reorder' | 'select' | 'top' | 'bottom';
  condition?: AbilityCondition;
  description: string;
}

// 能力発動条件
export interface AbilityCondition {
  type: string;                    // categoryPresent, powerMatch, tierMatch等
  target?: 'myBench' | 'opponentBench' | 'myDeck' | 'opponentDeck';
  category?: CardCategory | 'all';  // 参照属性: 'all' = 全属性対象
  tier?: CardTier;                  // 参照ティア: A, B, C
  comparison?: 'equal' | 'greaterOrEqual' | 'lessOrEqual';  // 参照判定: 同じ/以上/以下
  matchValue?: number;              // 参照数値(パワー値)
  minCount?: number;                // 必要枚数(0=不在条件)
  countMultiplier?: boolean;        // true=枚数×効果(複数), false=単体
}

// カード能力
export interface CardAbility {
  timing: AbilityTiming;
  effect: AbilityEffect;
}

// カード定義
export interface CardDef {
  id: string;
  name: string;
  basePower: number;
  category: CardCategory;
  tier: CardTier;
  abilities: CardAbility[];
  stockCount?: number;      // 山札の初期枚数
  rarity: CardRarity;
  flavorText?: string;
  // 旧互換
  unlockRound?: number;
}

// ゲーム中のカードインスタンス
export interface CardInstance {
  defId: string;
  instanceId: string;
  currentPower: number;
  isRevealed: boolean;
}

// カテゴリー表示名
export const CategoryNames: Record<CardCategory, string> = {
  castle: '城',
  city: 'シティ',
  space: '宇宙',
  movie: '映画',
  ship: '難破船',
  ghost: '幽霊',
  park: '遊園地',
};

// カテゴリーアイコン
export const CategoryIcons: Record<CardCategory, string> = {
  castle: '🏰',
  city: '🏙️',
  space: '🚀',
  movie: '🎬',
  ship: '🚢',
  ghost: '👻',
  park: '🎡',
};

// ティア表示名
export const TierNames: Record<CardTier, string> = {
  A: 'Tier A (基本)',
  B: 'Tier B (中級)',
  C: 'Tier C (上級)',
};

// ティア色
export const TierColors: Record<CardTier, string> = {
  A: '#2ecc71',   // 緑
  B: '#3498db',   // 青
  C: '#9b59b6',   // 紫
};
